import type {
  BetAction,
  ShowMeTheCoinAction,
  ShowMeTheCoinState,
  SubmitCoinsAction,
  ValidationResult,
} from "../types";
import {
  followerCountRange,
  hasCoins,
  isCoinDenomination,
  isLeadCountValid,
  isPlayerId,
  legalBetMoves,
} from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: ShowMeTheCoinState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) return fail("Unknown player", { playerId });
  if (state.phase === "game-over") return fail("Game is already over");
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

export function validateSubmitCoins(
  state: ShowMeTheCoinState,
  action: SubmitCoinsAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "submit-lead" && state.phase !== "submit-follow") {
    return fail("Coins can only be submitted during a submit phase", {
      phase: state.phase,
    });
  }

  for (const coin of action.coins) {
    if (!isCoinDenomination(coin)) {
      return fail("Invalid coin denomination", { coin });
    }
  }
  if (!hasCoins(state.inventory[action.playerId], action.coins)) {
    return fail("Not enough coins of that denomination in inventory");
  }

  const count = action.coins.length;
  if (state.phase === "submit-lead") {
    if (!isLeadCountValid(count)) {
      return fail("Lead must submit 2–6 coins", { count });
    }
  } else {
    const leadCount = state.submission[state.leadPlayerId]?.coins.length ?? 0;
    const { min, max } = followerCountRange(leadCount);
    if (count < min || count > max) {
      return fail("Follower must submit within ±1 of the lead's count", {
        count,
        leadCount,
        min,
        max,
      });
    }
  }
  return ok;
}

export function validateBet(
  state: ShowMeTheCoinState,
  action: BetAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "bet") {
    return fail("Betting is only allowed during the bet phase", {
      phase: state.phase,
    });
  }
  if (!legalBetMoves(state).includes(action.move)) {
    return fail("Illegal betting move", {
      move: action.move,
      legal: legalBetMoves(state),
    });
  }
  return ok;
}

export function validateAction(
  state: ShowMeTheCoinState,
  action: ShowMeTheCoinAction,
): ValidationResult {
  switch (action.type) {
    case "SubmitCoins":
      return validateSubmitCoins(state, action);
    case "Bet":
      return validateBet(state, action);
  }
}
